import React, { useState, useEffect } from 'react';
import { Plus, Trash2, UserPlus } from 'lucide-react';
import apiClient from '../../services/api';

interface School {
  _id: string;
  name: string;
  location: string;
  contactEmail: string;
  contactPhone: string;
  schoolId: string;
  postalCode: string;
  country: string;
  state: string;
  city: string;
  address: string;
  teachers: Teacher[];
}

interface Teacher {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  schoolId: string;
  status: 'active' | 'pending';
}

const SchoolManagement: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newSchool, setNewSchool] = useState({
    name: '',
    location: '',
    contactEmail: '',
    contactPhone: '',
    schoolId: '',
    postalCode: '',
    country: '',
    state: '',
    city: '',
    address: ''
  });
  const [newTeacher, setNewTeacher] = useState({
    firstName: '',
    lastName: '',
    email: '',
    schoolId: ''
  });
  const [selectedSchool, setSelectedSchool] = useState<string | null>(null);

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/admin/schools');
      setSchools(response.data.schools || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch schools');
      setSchools([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSchool = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Construct location from address details
    const location = `${newSchool.address}, ${newSchool.city}, ${newSchool.state}, ${newSchool.country} ${newSchool.postalCode}`;
    const schoolData = {
      ...newSchool,
      location
    };

    try {
      const response = await apiClient.post('/admin/schools', schoolData);
      setSchools([...schools, response.data.school]);
      setNewSchool({
        name: '',
        location: '',
        contactEmail: '',
        contactPhone: '',
        schoolId: '',
        postalCode: '',
        country: '',
        state: '',
        city: '',
        address: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add school');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeacher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSchool) {
      setError('Please select a school first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.post(`/admin/schools/${selectedSchool}/teachers`, {
        ...newTeacher,
        schoolId: selectedSchool
      });
      
      // Update the schools list with the new teacher
      setSchools(schools.map(school => 
        school._id === selectedSchool 
          ? { ...school, teachers: [...school.teachers, response.data.teacher] }
          : school
      ));

      setNewTeacher({
        firstName: '',
        lastName: '',
        email: '',
        schoolId: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add teacher');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {loading && (
        <div className="text-center py-4">
          <p>Loading schools...</p>
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Add School Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New School</h2>
            <form onSubmit={handleAddSchool} className="space-y-4">
              <div>
                <label htmlFor="schoolId" className="block text-sm font-medium text-gray-700">
                  School ID
                </label>
                <input
                  type="text"
                  id="schoolId"
                  value={newSchool.schoolId}
                  onChange={(e) => setNewSchool({ ...newSchool, schoolId: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="schoolName" className="block text-sm font-medium text-gray-700">
                  School Name
                </label>
                <input
                  type="text"
                  id="schoolName"
                  value={newSchool.name}
                  onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Address
                </label>
                <input
                  type="text"
                  id="address"
                  value={newSchool.address}
                  onChange={(e) => setNewSchool({ ...newSchool, address: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  value={newSchool.city}
                  onChange={(e) => setNewSchool({ ...newSchool, city: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  value={newSchool.state}
                  onChange={(e) => setNewSchool({ ...newSchool, state: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  value={newSchool.country}
                  onChange={(e) => setNewSchool({ ...newSchool, country: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                  Postal Code
                </label>
                <input
                  type="text"
                  id="postalCode"
                  value={newSchool.postalCode}
                  onChange={(e) => setNewSchool({ ...newSchool, postalCode: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                  Contact Email
                </label>
                <input
                  type="email"
                  id="contactEmail"
                  value={newSchool.contactEmail}
                  onChange={(e) => setNewSchool({ ...newSchool, contactEmail: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  value={newSchool.contactPhone}
                  onChange={(e) => setNewSchool({ ...newSchool, contactPhone: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {loading ? 'Adding...' : 'Add School'}
              </button>
            </form>
          </div>

          {/* Add Teacher Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Teacher</h2>
            <form onSubmit={handleAddTeacher} className="space-y-4">
              <div>
                <label htmlFor="school" className="block text-sm font-medium text-gray-700">
                  Select School
                </label>
                <select
                  id="school"
                  value={selectedSchool || ''}
                  onChange={(e) => setSelectedSchool(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                >
                  <option value="">Select a school</option>
                  {Array.isArray(schools) && schools.map((school) => (
                    school && school._id ? (
                      <option key={school._id} value={school._id}>
                        {school.name}
                      </option>
                    ) : null
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={newTeacher.firstName}
                  onChange={(e) => setNewTeacher({ ...newTeacher, firstName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={newTeacher.lastName}
                  onChange={(e) => setNewTeacher({ ...newTeacher, lastName: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={newTeacher.email}
                  onChange={(e) => setNewTeacher({ ...newTeacher, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading || !selectedSchool}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {loading ? 'Adding...' : 'Add Teacher'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Schools List */}
      {!loading && !error && Array.isArray(schools) && schools.length > 0 && (
        <div className="mt-8 bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Registered Schools</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {schools.map((school) => (
              school && school._id ? (
                <div key={school._id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">{school.name}</h4>
                      <p className="mt-1 text-sm text-gray-500">{school.location}</p>
                    </div>
                    <button
                      onClick={() => {/* Handle delete school */}}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                  
                  {/* Teachers List */}
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Teachers</h5>
                    <div className="space-y-2">
                      {Array.isArray(school.teachers) && school.teachers.map((teacher) => (
                        teacher && teacher._id ? (
                          <div key={teacher._id} className="flex justify-between items-center text-sm">
                            <span>
                              {teacher.firstName} {teacher.lastName} ({teacher.email})
                            </span>
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-800">
                              {teacher.status}
                            </span>
                          </div>
                        ) : null
                      ))}
                    </div>
                  </div>
                </div>
              ) : null
            ))}
          </div>
        </div>
      )}

      {!loading && !error && (!Array.isArray(schools) || schools.length === 0) && (
        <div className="mt-8 text-center text-gray-500">
          No schools registered yet.
        </div>
      )}
    </div>
  );
};

export default SchoolManagement; 